import { apiFetch } from './client';
import type { AvailableSpot } from './available-spots';
import type { UsageStatus, UsageType } from './my-parking-spots';

export interface AiChatRequest {
  message: string;
  conversationId?: string;
}

export interface AiUsageCard {
  id: string;
  spotId: string;
  spotCode: string;
  type: UsageType;
  startAt: string;
  endAt: string;
  status: UsageStatus;
  role?: 'requester' | 'owner';
  requesterLabel?: string;
  canCancel: boolean;
  canApprove?: boolean;
  canReject?: boolean;
}

export interface AiOwnedSpotCard {
  id: string;
  spotId: string;
  spotCode: string;
  location: string | null;
  level: string | null;
  canLend: boolean;
}

export interface AiWindowCard {
  id: string;
  spotId: string;
  spotCode: string;
  startAt: string;
  endAt: string;
  allowBorrow: boolean;
  allowRent: boolean;
  seriesId: string | null;
  canCancel: boolean;
}

export interface AiBulkRequestCard {
  id: string;
  startAt: string;
  endAt: string;
  type: UsageType;
  expiresAt: string;
  canCancel: boolean;
}

export interface AiMisuseCard {
  id: string;
  spotId: string;
  spotCode: string;
  licensePlate: string;
  createdAt: string;
  canResolve: boolean;
}

export interface AiChatResponse {
  conversationId: string;
  message: string;
  spotCards?: AvailableSpot[];
  usageCards?: AiUsageCard[];
  ownedSpotCards?: AiOwnedSpotCard[];
  windowCards?: AiWindowCard[];
  bulkRequestCards?: AiBulkRequestCard[];
  misuseCards?: AiMisuseCard[];
  reservationCreated?: boolean;
  suggestBulkRequest?: boolean;
  suggestReportMisuse?: boolean;
  refreshParkingLists?: boolean;
}

export interface AiConversationSummary {
  id: string;
  title: string | null;
  lastMessageAt: string;
  preview: string;
}

export interface AiConversationMessage {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface AiConversationListResponse {
  conversations: AiConversationSummary[];
}

export interface AiConversationMessagesResponse {
  conversationId: string;
  messages: AiConversationMessage[];
  nextBefore: string | null;
}

export const aiKeys = {
  all: ['ai'] as const,
  conversations: () => [...aiKeys.all, 'conversations'] as const,
  messages: (conversationId: string) => [...aiKeys.all, 'messages', conversationId] as const,
};

export function postAiChat(body: AiChatRequest): Promise<AiChatResponse> {
  return apiFetch<AiChatResponse>('/ai/chat', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getAiConversations(): Promise<AiConversationListResponse> {
  return apiFetch<AiConversationListResponse>('/ai/conversations');
}

export function getAiConversationMessages(
  conversationId: string,
  params?: { limit?: number; before?: string },
): Promise<AiConversationMessagesResponse> {
  const search = new URLSearchParams();
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.before) search.set('before', params.before);
  const query = search.toString();
  return apiFetch<AiConversationMessagesResponse>(
    `/ai/conversations/${encodeURIComponent(conversationId)}/messages${query ? `?${query}` : ''}`,
  );
}

export async function deleteAiConversation(conversationId: string): Promise<void> {
  await apiFetch<void>(`/ai/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
  });
}
